import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Linkify from "react-linkify";
import { makeStyles } from '@material-ui/core/styles';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  CircularProgress
} from '@material-ui/core';
import { getThread, getThreadComment } from "../../actions/threadActions";
import { setRecordInfo } from "../../actions/mailActions";
import history from "../../history";

import Image from "../common/Image";
import JobDetailCompanyInfo from "../job/JobDetail/JobDetailCompanyInfo";
import ThreadCommentArea from "./ThreadCommentArea";

const useStyles = makeStyles((theme) => ({
  threadDetail: {
    padding: theme.spacing(2),
  },
  heading: {
    fontSize: '2.4rem',
    fontWeight: 700,
    color: theme.palette.primary.dark,
    marginBottom: theme.spacing(3),
    textAlign: 'center',
  },
  contentContainer: {
    display: 'flex',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  mainContent: {
    flexGrow: 1,
    padding: theme.spacing(2),
  },
  detailLabel: {
    fontSize: '1.8rem',
    fontWeight: 700,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(2),
    borderBottom: `2px solid ${theme.palette.primary.main}`,
    paddingBottom: theme.spacing(1),
  },
  imageContainer: {
    marginBottom: theme.spacing(2),
    '& img': {
      maxWidth: '100%',
      height: 'auto',
      borderRadius: theme.shape.borderRadius,
      boxShadow: theme.shadows[2],
    },
  },
  detailsText: {
    fontSize: '1.6rem',
    lineHeight: 1.8,
    whiteSpace: 'pre-wrap',
    color: theme.palette.text.primary,
    '& a': {
      color: theme.palette.primary.main,
      textDecoration: 'underline',
      '&:hover': {
        color: theme.palette.primary.dark,
      },
    },
  },
  userInfoContainer: {
    minWidth: '300px',
    [theme.breakpoints.down('md')]: {
      minWidth: 'auto',
    },
  },
  mailButton: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
    fontSize: '1.4rem',
    fontWeight: 600,
    padding: theme.spacing(1.5, 3),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    '&:hover': {
      backgroundColor: theme.palette.success.dark,
      transform: 'translateY(-1px)',
      boxShadow: theme.shadows[4],
    },
    transition: 'all 0.2s ease-in-out',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
  },
  noDataContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
    fontSize: '1.8rem',
    color: theme.palette.text.secondary,
  },
  section: {
    marginTop: theme.spacing(4),
  },
}));

const ThreadDetail = props => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const threadId = props.match.params.id;
  const currentUser = useSelector(state => state.user.currentUser);
  const threadDetail = useSelector(state => state.thread.selectedThread);
  const loading = useSelector(state => state.common.loading);

  useEffect(() => {
    if (threadId) {
      dispatch(getThread(threadId));
    }
    const interval = setInterval(() => {
      if (threadId) {
        dispatch(getThreadComment(threadId));
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [threadId, dispatch]);

  const handleSendMail = () => {
    dispatch(
      setRecordInfo({
        id: threadDetail._id,
        type: "thread",
        title: "RE:" + threadDetail.title,
        details: threadDetail.details,
        userName: threadDetail.user.name
      })
    );
    history.push("/mail");
  };

  const renderContent = () => {
    if (loading)
      return (
        <Box className={classes.loadingContainer}>
          <CircularProgress size={60} />
        </Box>
      );
    if (!threadDetail) return (
      <Box className={classes.noDataContainer}>
        <Typography variant="h6">No data</Typography>
      </Box>
    );

    return (
      <Box className={classes.threadDetail}>
        <Typography variant="h1" component="h1" className={classes.heading}>
          {threadDetail.title}
        </Typography>

        <Box className={classes.contentContainer}>
          <Box className={classes.mainContent}>
            {threadDetail.mainImage ? (
              <Box className={classes.imageContainer}>
                <Image src={threadDetail.mainImage.image_url} />
              </Box>
            ) : null}

            <Typography className={classes.detailsText}>
              <Linkify>{threadDetail.details}</Linkify>
            </Typography>
          </Box>

          <Box className={classes.userInfoContainer}>
            <JobDetailCompanyInfo user={threadDetail.user} />
            {threadDetail.email &&
              currentUser &&
              threadDetail.user._id !== currentUser._id ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handleSendMail}
                className={classes.mailButton}
                fullWidth
              >
                投稿者にメールを送る
              </Button>
            ) : null}

          </Box>
        </Box>

        <Box className={classes.section}>
          <ThreadCommentArea
            threadId={threadDetail._id}
            commentList={threadDetail.comments}
            threadOwnerId={threadDetail.user._id}
            currentUserId={currentUser ? currentUser._id : null}
          />
        </Box>
      </Box>
    );
  };

  return <Container maxWidth="lg">{renderContent()}</Container>;
};

export default ThreadDetail;
